/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import MarketoFormModal from "@stoneage/App/Components/MarketoFormModal/MarketoFormModal";
import { getMarketoSettings } from "@stoneage/App/Store/Selectors/MarketoSettings";
import { constants } from "@stoneage/XDL/constants";
import { marketoDefaultCss } from "@stoneage/XDL/Utilities/SharedStyles/sharedStyles";
import * as cssLinter from "css";
import React, { FC } from "react";

type Props = WidgetProps;

const MarketoForm: FC<Props> = ({
    fields: { title, formDescription, displayAs, height, width, buttonText, formId, instructionMessage, formType, customCss },
    id,
}) => {
    const { formSettings } = useAppSelector(state => {
        const marketoSettings = getMarketoSettings(state);
        return {
            formSettings: {
                baseUrl: marketoSettings.baseUrl,
                munchkinId: marketoSettings.munchkinId,
                formId,
            },
        };
    });

    return (
        <MarketoFormModal
            title={title}
            formDescription={formDescription}
            displayAs={displayAs}
            height={height}
            width={width}
            buttonText={buttonText}
            marketoSettings={formSettings}
            instructionMessage={instructionMessage}
            customCSS={customCss}
            formType={formType}
        />
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advnacedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: MarketoForm,
    definition: {
        group: "XNGAGE" as any,
        displayName: "Marketo Form",
        fieldDefinitions: [
            {
                name: "title",
                displayName: "Form Title",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: "formDescription",
                displayName: "Body Copy",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: "displayAs",
                displayName: "Display As",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Button",
                        value: "button",
                        tooltip: "This will display a button which will open a modal containing the form",
                    },
                    {
                        displayName: "Form",
                        value: "form",
                        tooltip: "This will display the complete form on the page directly",
                    },
                ],
                defaultValue: "",
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: "buttonText",
                displayName: "Button Text",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isVisible: ({ fields }) => fields.displayAs === "button",
                isRequired: true,
                tab: basicTab,
            },
            {
                name: "height",
                displayName: "Form Height in Pixels",
                editorTemplate: "IntegerField",
                defaultValue: constants.marketoModalDefaultDimensions.height,
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: "width",
                displayName: "Form Width in Pixels",
                editorTemplate: "IntegerField",
                defaultValue: constants.marketoModalDefaultDimensions.width,
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: "formId",
                displayName: "Form ID",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
            },
            
            {
                name: "formType",
                displayName: "Form Type",
                editorTemplate: "RadioButtonsField",
                tooltip: "This is used to determine data sent to google analytics on form submissions",
                options: [
                    {
                        displayName: "Contact Us",
                        value: "contactUs",
                    },
                    {
                        displayName: "Newsletter Subscription",
                        value: "newsSubscription",
                    },
                    {
                        displayName: "Book Consultation",
                        value: "bookConsultation",
                    },
                    {
                        displayName: "Other",
                        value: "other",
                    },
                ],
                defaultValue: "",
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: "instructionMessage",
                displayName: "Form Success Copy",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: "customCss",
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                defaultValue: marketoDefaultCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
                tab: advnacedTab,
            },
        ],
    },
};

export default widgetModule;
